import { useMemo } from 'react'
import { getPersonAboutData } from '../services/services'
import useReactQuery from './useReactQuery'
import { hasEstimatedLifespan } from '../utils/personLifespan'

const LIFESPAN_YEAR_REGEX = /(\d{4})/g
const DATE_YEAR_REGEX = /(\d{4})/
const PERSON_ABOUT_DATA_QUERY_KEY = 'person-about-data'

const getPersonAboutDataQueryKey = (personId) => [PERSON_ABOUT_DATA_QUERY_KEY, personId]

function toNumber(value) {
  const parsedValue = Number.parseInt(value, 10)
  return Number.isFinite(parsedValue) ? parsedValue : null
}

function toYear(value) {
  const directYear = toNumber(value?.year ?? value?.startYear)
  if (Number.isFinite(directYear)) return directYear

  const dateYear = DATE_YEAR_REGEX.exec(`${value?.date ?? ''}`)?.[1]
  return toNumber(dateYear)
}

const INVALID_IMAGE_URL_PATTERN = /^(null|undefined)(\/|$|\?)/i

const sanitizeImageUrl = (value) => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  // Guard against stringified null/undefined values produced when an upstream
  // template like `${origin}/dist.jpg` resolves to e.g. "null/dist.jpg".
  if (INVALID_IMAGE_URL_PATTERN.test(trimmed)) return ''
  return trimmed
}

export function getImageLink(value) {
  if (typeof value === 'string') return sanitizeImageUrl(value)
  return sanitizeImageUrl(value?.url ?? value?.link ?? value?.href ?? '')
}

export function getPortraitUrl(personData) {
  return (
    getImageLink(personData?.portraitUrl) ||
    getImageLink(personData?.portrait) ||
    getImageLink(personData?.portraitThumbnailLink) ||
    getImageLink(personData?.portraitImageLink) ||
    getImageLink(personData?.familySearchPortraitUrl) ||
    getImageLink(personData?.imageUrl) ||
    getImageLink(personData?.imageLink) ||
    null
  )
}

function getConclusionType(gender) {
  if (gender === 'Male') return 'http://gedcomx.org/Male'
  if (gender === 'Female') return 'http://gedcomx.org/Female'
  return 'http://gedcomx.org/Unknown'
}

function parseLifespan(lifespan) {
  const value = `${lifespan ?? ''}`.trim()
  const years = [...value.matchAll(LIFESPAN_YEAR_REGEX)].map(([year]) => Number.parseInt(year, 10))
  const birthYear = Number.isFinite(years[0]) ? years[0] : null
  const deathYear = Number.isFinite(years[1]) ? years[1] : null

  return {
    birthYear,
    deathYear,
    living: /living/i.test(value),
  }
}

function buildFamilyMember(personData) {
  const id = personData?.identifier || personData?.id
  if (!personData || !id) return null
  const { birthYear, deathYear, living } = parseLifespan(personData.lifespan)
  const personLifespan = { birthYear, deathYear, living }

  return {
    id,
    name: personData.name || '',
    gender: personData.gender || 'Unknown',
    conclusionType: getConclusionType(personData.gender),
    lifespan: personData.lifespan || '',
    portraitUrl: getPortraitUrl(personData),
    birthYear,
    deathYear,
    living,
    isEstimatedLifespan: hasEstimatedLifespan(personLifespan),
  }
}

function buildMarriageEvent(marriage) {
  if (!marriage) return null
  return {
    type: 'MARRIAGE',
    details: {
      date: marriage.date || null,
      place: marriage.place || null,
    },
    ...marriage,
  }
}

function buildHistoricEvent(event, eventIndex) {
  const year = toYear(event)
  if (!Number.isFinite(year)) return null

  return {
    id: event?.id || `historic-event-${eventIndex}`,
    year,
    age: toNumber(event?.age),
    julianDay: toNumber(event?.julianDay),
    title: event?.title || '',
    description: event?.description || '',
    detailedDescription: event?.prose || event?.description || '',
    imageUrl: getImageLink(event?.imageUrl) || getImageLink(event?.image) || null,
    url: event?.url || '',
    urlTitle: event?.urlTitle || '',
    jurisdictionLevel: event?.jurisdictionLevel || '',
  }
}

function getEventArray(value) {
  return Array.isArray(value) ? value : []
}

function getEventsWithJurisdiction(events, jurisdictionLevel) {
  return getEventArray(events).map((event) => ({
    ...event,
    jurisdictionLevel: event?.jurisdictionLevel || jurisdictionLevel,
  }))
}

function isHistoricTimelineEvent(event) {
  const haystack = `${event?.eventType ?? ''} ${event?.type ?? ''} ${event?.category ?? ''} ${
    event?.jurisdictionLevel ?? ''
  }`
    .trim()
    .toLowerCase()

  return /historic|historical|world|regional/.test(haystack)
}

function getHistoricTimelineEvents(timelineEvents) {
  return getEventArray(timelineEvents)
    .filter(isHistoricTimelineEvent)
    .map((event) => ({
      id: event?.id,
      year: event?.year,
      date: event?.date,
      title: event?.title,
      description: event?.description || event?.prose || '',
      detailedDescription: event?.prose || event?.description || '',
      imageUrl: event?.imageUrl,
      url: event?.url,
      urlTitle: event?.urlTitle,
      jurisdictionLevel: event?.jurisdictionLevel || event?.category || '',
      age: event?.age ?? event?.ancestorAge,
      julianDay: event?.julianDay,
    }))
}

function getHistoricEventCandidates(localizedData) {
  const historicalEvents = localizedData?.historicalEvents || {}

  return [
    ...getEventArray(localizedData?.historicEvents),
    ...getHistoricTimelineEvents(localizedData?.timeline),
    ...getEventArray(localizedData?.historicalEvents),
    ...getEventsWithJurisdiction(localizedData?.worldEvents, 'world'),
    ...getEventsWithJurisdiction(localizedData?.regionalEvents, 'regional'),
    ...getEventsWithJurisdiction(localizedData?.worldHistoricEvents, 'world'),
    ...getEventsWithJurisdiction(localizedData?.regionalHistoricEvents, 'regional'),
    ...getEventsWithJurisdiction(historicalEvents?.world, 'world'),
    ...getEventsWithJurisdiction(historicalEvents?.regional, 'regional'),
  ]
}

function getLocalizedData(data) {
  const localizedData = data?.localizedData || {}
  if (localizedData.en) return localizedData.en

  const firstLocaleKey = Object.keys(localizedData)[0]
  return localizedData[firstLocaleKey] || {}
}

function buildParentFamily(person, personId) {
  if (!person.parent?.length) return []

  const maleParent = person.parent.find((p) => p.gender === 'Male')
  const femaleParent = person.parent.find((p) => p.gender === 'Female')
  const otherParents = person.parent.filter((p) => p.gender !== 'Male' && p.gender !== 'Female')

  const parent1 = maleParent || otherParents[0] || null
  const parent2 = femaleParent || (maleParent ? otherParents[0] : otherParents[1]) || null

  const parent1Member = buildFamilyMember(parent1)
  const parent2Member = buildFamilyMember(parent2)

  if (!parent1Member && !parent2Member) return []

  const siblings = (person.sibling || [])
    .filter((s) => s.identifier !== personId)
    .map(buildFamilyMember)
    .filter(Boolean)

  return [
    {
      parent1: parent1Member,
      parent2: parent2Member,
      parentMarriage: person.parentMarriage,
      children: siblings,
    },
  ]
}

function buildSpouseFamilies(person, personId) {
  if (!person.spouse?.length) return []

  return person.spouse.map((spouse) => {
    const childrenForSpouse = (person.children || [])
      .filter((c) => c.otherParentId === spouse.identifier)
      .map(buildFamilyMember)
      .filter(Boolean)

    return {
      parent1: buildFamilyMember({
        id: personId,
        name: person.name,
        gender: person.gender,
        lifespan: person.lifespan,
        portrait: person.portrait,
      }),
      parent2: buildFamilyMember(spouse),
      event: buildMarriageEvent(spouse.marriage),
      children: childrenForSpouse,
    }
  })
}

function transformAboutData(data, personId) {
  const person = data?.person || {}
  const localizedData = getLocalizedData(data)

  const sources = (person.sourceList || []).map((source) => ({
    sourceUri: source.sourceUri,
    title: source.title,
    sourceLink: source.sourceLink,
    sourceImageLink: source.sourceImageLink,
    sourceThumbnailLink: source.sourceThumbnailLink,
    sourceType: source.sourceType,
  }))

  const families = {
    parents: buildParentFamily(person, personId),
    spouses: buildSpouseFamilies(person, personId),
  }

  const timelineEvents = (localizedData.timeline || []).map((event) => ({
    id: event.id,
    eventType: event.eventType,
    title: event.title,
    date: event.date,
    year: event.year,
    place: event.place,
    prose: event.prose,
    description: event.description,
    relativeName: event.relativeName,
    relativeId: event.relativeId,
    relativeSex: event.relativeSex,
    imageUrl: event.imageUrl,
    coordinates: event.coordinates,
    ancestorAge: event.ancestorAge,
  }))

  const historicEvents = getHistoricEventCandidates(localizedData)
    .map((event, eventIndex) => buildHistoricEvent(event, eventIndex))
    .filter(Boolean)

  return {
    person: {
      id: personId,
      name: person.name,
      alternateName: person.alternateName,
      gender: person.gender,
      lifespan: person.lifespan,
      fullLifespan: person.fullLifespan,
      birthDate: person.birthDate,
      birthPlace: person.birthPlace,
      deathDate: person.deathDate,
      deathPlace: person.deathPlace,
      burialPlace: person.burialPlace,
      portrait: person.portrait,
      ageAtDeath: person.ageAtDeath,
      sourcesCount: person.sources,
    },
    sources,
    families,
    lifeSketch: localizedData.lifeSummary || '',
    nameMeanings: localizedData.nameMeanings || [],
    timelineEvents,
    historicEvents,
  }
}

export function usePersonAboutData(personId) {
  const { data, query } = useReactQuery({
    queryKey: getPersonAboutDataQueryKey(personId),
    queryFn: () => getPersonAboutData({ personId }),
    enabled: !!personId,
  })

  const transformedData = useMemo(() => transformAboutData(data, personId), [data, personId])

  return {
    ...transformedData,
    loading: query?.isLoading,
  }
}

export function usePeopleAboutData(personIds = []) {
  const normalizedPersonIds = useMemo(() => [...new Set((personIds ?? []).filter(Boolean))], [personIds])
  const singlePersonId = normalizedPersonIds.length === 1 ? normalizedPersonIds[0] : null

  const { data, query } = useReactQuery({
    queryKey: singlePersonId ? getPersonAboutDataQueryKey(singlePersonId) : ['people-about-data', normalizedPersonIds],
    queryFn: () => {
      if (singlePersonId) return getPersonAboutData({ personId: singlePersonId })

      return Promise.all(
        normalizedPersonIds.map(async (personId) => {
          const response = await getPersonAboutData({ personId })
          return {
            personId,
            data: response?.data,
          }
        })
      ).then((peopleData) => ({ data: peopleData }))
    },
    enabled: normalizedPersonIds.length > 0,
  })

  const peopleAboutData = useMemo(() => {
    if (singlePersonId) {
      return data
        ? [
            {
              personId: singlePersonId,
              ...transformAboutData(data, singlePersonId),
            },
          ]
        : []
    }

    return (data ?? []).map(({ personId, data: personData }) => ({
      personId,
      ...transformAboutData(personData, personId),
    }))
  }, [data, singlePersonId])

  return {
    peopleAboutData,
    loading: query?.isLoading,
  }
}
